import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Mail, Inbox, Send } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

type Row = {
  id: string;
  deal_id: string;
  channel: 'sms' | 'email';
  direction: 'in' | 'out';
  subject: string | null;
  body_text: string | null;
  from_addr: string;
  to_addr: string;
  created_at: string;
  sent_at: string | null;
  received_at: string | null;
  deals?: { customer_name: string | null } | null;
};

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function RecentConversationsCard() {
  const { mode } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (mode !== 'real') { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('messages')
        .select('id, deal_id, channel, direction, subject, body_text, from_addr, to_addr, created_at, sent_at, received_at, deals(customer_name)')
        .order('created_at', { ascending: false })
        .limit(6);
      if (!cancelled) {
        setRows((data as unknown as Row[]) ?? []);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode]);

  if (mode !== 'real') return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" /> Recent Conversations
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No messages yet. Reps can text or email customers from any deal.
          </p>
        ) : (
          <div className="space-y-3">
            {rows.map(r => {
              const ChannelIcon = r.channel === 'email' ? Mail : MessageSquare;
              const DirIcon = r.direction === 'in' ? Inbox : Send;
              const dirColor = r.direction === 'in' ? 'text-green-400' : 'text-muted-foreground';
              const customer = r.deals?.customer_name ?? 'Unknown';
              const preview = r.subject || r.body_text || '';
              const ts = r.received_at || r.sent_at || r.created_at;
              return (
                <Link
                  key={r.id}
                  to={`/deals/${r.deal_id}`}
                  className="flex items-start gap-3 p-2 rounded hover:bg-muted/30 transition-colors"
                >
                  <div className="flex flex-col items-center gap-1 shrink-0 pt-0.5">
                    <ChannelIcon className="h-4 w-4 text-primary" />
                    <DirIcon className={`h-3 w-3 ${dirColor}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-sm font-medium truncate">{customer}</p>
                      <p className="text-[10px] text-muted-foreground/60 shrink-0">{timeAgo(ts)}</p>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{preview}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}